import React from 'react';
import Icon from '../../../components/AppIcon';

const ActiveAssessments = ({ assessments, onViewResults, detailed = false }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'draft':
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
      case 'scheduled':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'mandatory':
        return 'bg-red-500/10 text-red-500';
      case 'certification':
        return 'bg-purple-500/10 text-purple-500';
      case 'skill-based':
        return 'bg-blue-500/10 text-blue-500';
      default:
        return 'bg-gray-500/10 text-gray-500';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">Active Assessments</h2>
          <div className="flex items-center gap-2">
            <button className="p-2 hover:bg-muted rounded-lg transition-colors">
              <Icon name="Filter" size={18} />
            </button>
            <button className="p-2 hover:bg-muted rounded-lg transition-colors">
              <Icon name="Search" size={18} />
            </button>
          </div>
        </div>
      </div>
      <div className="divide-y divide-border">
        {assessments?.map((assessment) => {
          const completionRate = ((assessment?.completed / assessment?.participants) * 100)?.toFixed(1);
          
          return (
            <div key={assessment?.id} className="p-6 hover:bg-muted/50 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">{assessment?.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(assessment?.status)}`}>
                      {assessment?.status}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(assessment?.type)}`}>
                      {assessment?.type}
                    </span>
                  </div>
                  <div className="flex items-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Users" size={16} />
                      <span>{assessment?.participants} participants</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={16} />
                      <span>{assessment?.duration} min</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="HelpCircle" size={16} />
                      <span>{assessment?.questions} questions</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Target" size={16} />
                      <span>{assessment?.passingScore}% to pass</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onViewResults?.(assessment)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                  >
                    <Icon name="BarChart3" size={16} />
                    View Results
                  </button>
                  <button className="p-2 hover:bg-background rounded-lg transition-colors">
                    <Icon name="MoreVertical" size={18} />
                  </button>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-3">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Completion Progress</span>
                  <span className="font-medium text-foreground">
                    {assessment?.completed}/{assessment?.participants} ({completionRate}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${completionRate}%` }}
                  />
                </div>
              </div>

              {/* Deadline */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Calendar" size={16} />
                  <span>Deadline: {assessment?.deadline}</span>
                </div>
                {detailed && (
                  <div className="flex items-center gap-2">
                    <button className="text-sm text-primary hover:underline">Edit</button>
                    <button className="text-sm text-primary hover:underline">Duplicate</button>
                    <button className="text-sm text-destructive hover:underline">Archive</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActiveAssessments;